import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ResponsiveSheet from "../ResponsiveSheet";
import ArticuloContenido, { CATEGORIA_ICONOS, categoriaDe } from "../ArticuloContenido";
import { AYUDA_ARTICULOS } from "../../content/ayudaArticulos";
import { AYUDA_CONTEXTOS_PANTALLA } from "../../content/ayudaContextos";

// Ver TAREA: ayuda contextual del CRM. Mismo patrón que
// AyudaContextualSheet.jsx de Citas: reusa ResponsiveSheet (SwipeableDrawer
// móvil / Dialog desktop) y ArticuloContenido (el mismo bloque que pinta
// un artículo en /ayuda -- ningún texto duplicado entre el índice general
// y esta hoja).
//
// Navegación interna de 2 pantallas dentro de la MISMA hoja (índice de
// artículos de esta pantalla -> artículo con back) -- el usuario tiene la
// pantalla real detrás mientras sigue los pasos, nunca se le saca de
// contexto.
export default function AyudaContextualSheet({ open, onClose, pantallaId }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === "light";
  const navigate = useNavigate();
  const [articuloActivoSlug, setArticuloActivoSlug] = useState(null);

  // Reinicia al índice cada vez que se abre -- si el usuario cerró leyendo
  // un artículo, la próxima vez que toque el ícono empieza del índice de
  // esa pantalla, no donde se quedó.
  useEffect(() => {
    if (open) setArticuloActivoSlug(null);
  }, [open]);

  const contexto = AYUDA_CONTEXTOS_PANTALLA[pantallaId];
  const articulos = AYUDA_ARTICULOS.filter((a) => a.pantallas?.includes(pantallaId));
  const articulo = articuloActivoSlug ? AYUDA_ARTICULOS.find((a) => a.slug === articuloActivoSlug) : null;
  const bordeSuave = isLight ? "rgba(0,0,0,0.07)" : "rgba(255,255,255,0.08)";
  const fondoAcento = isLight ? "rgba(3,153,255,0.07)" : "rgba(3,153,255,0.12)";

  const irATodos = () => {
    onClose();
    navigate("/ayuda");
  };

  return (
    <ResponsiveSheet open={open} onClose={onClose} desktopWidth={440} maxHeightMobile="85vh">
      {articulo ? (
        <Box sx={{ display: "flex", flexDirection: "column", overflow: "hidden", maxHeight: "100%" }}>
          <Box
            component="button" onClick={() => setArticuloActivoSlug(null)}
            sx={{
              display: "flex", alignItems: "center", gap: "8px", p: "12px 20px", border: "none", background: "transparent",
              borderBottom: `0.5px solid ${bordeSuave}`, cursor: "pointer", textAlign: "left", flexShrink: 0,
              fontSize: 13.5, fontWeight: 700, color: isLight ? "#111" : "rgba(255,255,255,0.88)",
            }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 17, flexShrink: 0 }} />
            Ayuda de esta pantalla
          </Box>
          <Box sx={{ px: 2.5, py: 2, overflowY: "auto" }}>
            <ArticuloContenido articulo={articulo} onAbrirArticulo={setArticuloActivoSlug} isLight={isLight} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ px: 2.5, pb: 2.5, pt: "4px", overflowY: "auto" }}>
          {contexto && (
            <Box sx={{
              fontSize: 13, lineHeight: 1.55, color: isLight ? "#666" : "rgba(255,255,255,0.6)",
              background: fondoAcento, borderRadius: "12px", p: "13px 14px", mb: 2,
            }}>
              {contexto}
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: 2 }}>
            {articulos.map((a) => {
              const Icon = CATEGORIA_ICONOS[a.categoria];
              return (
                <Box
                  key={a.slug} onClick={() => setArticuloActivoSlug(a.slug)}
                  sx={{
                    display: "flex", alignItems: "center", gap: "10px", p: "11px 12px", borderRadius: "9px",
                    background: isLight ? "#f8f7f5" : "rgba(255,255,255,0.03)",
                    border: `0.5px solid ${bordeSuave}`, cursor: "pointer",
                    "&:hover": { borderColor: "#0399FF" },
                  }}
                >
                  <Box sx={{
                    width: 28, height: 28, borderRadius: "8px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center",
                    background: fondoAcento, color: "#0399FF",
                  }}>
                    {Icon && <Icon sx={{ fontSize: 14 }} />}
                  </Box>
                  <Box sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35, color: isLight ? "#111" : "rgba(255,255,255,0.88)" }}>
                    {a.titulo}
                  </Box>
                  <ChevronRightRoundedIcon sx={{ fontSize: 15, color: isLight ? "#ccc" : "rgba(255,255,255,0.3)", flexShrink: 0, ml: "auto" }} />
                </Box>
              );
            })}
          </Box>

          <Box
            component="button" onClick={irATodos}
            sx={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", width: "100%",
              fontSize: 12.5, fontWeight: 700, color: "#0399FF", textDecoration: "none",
              p: "11px", borderRadius: "9px", border: `0.5px solid ${bordeSuave}`, background: "transparent", cursor: "pointer", fontFamily: "inherit",
              "&:hover": { borderColor: "#0399FF" },
            }}
          >
            Ver todos los artículos
            <ChevronRightRoundedIcon sx={{ fontSize: 14 }} />
          </Box>
        </Box>
      )}
    </ResponsiveSheet>
  );
}
